import { useState } from 'react'
import { motion } from 'framer-motion'

const factors = [
  {
    code: 'AQ',
    name: 'Adversity Quotient',
    tagline: 'Resilience under pressure',
    desc: 'Have they built through genuine constraint? Can they lead with clarity when the global parent is unsure, the team is half-formed, and the answer is still unknown?',
    position: 'top-left',
  },
  {
    code: 'IQ',
    name: 'Intelligence Quotient',
    tagline: 'Technical & domain depth',
    desc: 'Is their technical credibility deep enough to earn the respect of a senior engineering team from day one? We assess substance, not credentials.',
    position: 'top-right',
  },
  {
    code: 'SQ',
    name: 'Social Quotient',
    tagline: 'Network & industry influence',
    desc: 'Do they have the professional network to accelerate hiring in the first 90 days — before the employer brand exists?',
    position: 'bottom-left',
  },
  {
    code: 'EQ',
    name: 'Emotional Quotient',
    tagline: 'Cross-cultural leadership',
    desc: 'Can they manage the cultural complexity of cross-timezone leadership — aligning a sceptical global team with an ambitious local operation?',
    position: 'bottom-right',
  },
]

export default function FourFactorSection() {
  const [hovered, setHovered] = useState(null)

  return (
    <section
      data-testid="four-factor-section"
      id="four-factor"
      className="py-32 md:py-40 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          {/* Left: header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-[10px] tracking-[0.35em] uppercase text-white/25 mb-6 font-body font-semibold">
              Proprietary Framework
            </p>
            <h2 className="text-[clamp(2.5rem,5vw,5rem)] font-heading font-black tracking-tighter leading-none text-white mb-8">
              The Four-Factor<br />
              <span className="font-light text-white/55">Assessment</span>
            </h2>
            <p className="text-sm font-body text-white/45 leading-relaxed max-w-md mb-10">
              Every leader we assess is evaluated across four axes — not as a checkbox, but as a holistic picture of how they will perform in the specific context of your GCC, at this stage of its development.
            </p>
            <div className="w-10 h-px bg-white/20" />
          </motion.div>

          {/* Right: interactive 2x2 grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
          >
            <div className="grid grid-cols-2 max-w-lg">
              {factors.map((f, i) => (
                <motion.div
                  key={f.code}
                  data-testid={`factor-${f.code.toLowerCase()}`}
                  className="border border-white/10 p-6 md:p-8 cursor-default"
                  animate={{
                    opacity: hovered === null || hovered === i ? 1 : 0.18,
                    borderColor: hovered === i ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
                  }}
                  transition={{ duration: 0.25 }}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <p className="text-3xl font-heading font-black text-white/30 mb-4 leading-none">{f.code}</p>
                  <p className="text-xs font-body font-semibold text-white/70 tracking-wide mb-1">{f.name}</p>
                  <p className="text-[10px] font-body text-white/40 mb-3 tracking-wide">{f.tagline}</p>
                  <p className="text-xs font-body text-white/50 leading-relaxed hidden md:block">{f.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
